/**
 * disc_system — the discs of a system's plane (natural objects, 2026-10-07; catalog SB-15, NE-10, NE-08): the zodiacal
 * DUST BAND of an inner system, a DEBRIS DISC beyond the planets, the PROTOPLANETARY DISC of a forming one with a gap
 * swept at each planet's orbit.
 *
 * ONE MATERIAL, A VOLUME MARCH. A disc is not a textured plane (a plane has no inside, and a ship flies through these):
 * it is a density ρ(r)·e^(−y²/2H(r)²) about the star, marched along the view ray inside the cylinder that bounds it.
 * Everything radial — the density with its gaps, the scale height, the optical depth of the star's own light out to r —
 * is baked ONCE on the CPU into a 512-texel table (world_look.bakeDiscTable, the pure rule and the shader's only
 * lookup). Per step: single scattering of the star's light (Henyey–Greenstein — a band is brightest looked at TOWARD
 * the star), shadowed by the disc itself, a warm glow at a forming disc's inner rim, the glow of its lit SURFACE layer
 * (the midplane under it is a dark lane seen edge-on), and the extinction of what is behind — each step giving its
 * source × what it stops (energy-conserving: 8 steps draw what 24 draw).
 *
 * THE RING SHEET. A forming disc's rings are its settled grains — a layer far thinner than the gas, so it is not marched:
 * the ray's ONE crossing of the midplane is shaded from the table (crisp gaps at any angle, no slices), behind the haze
 * in front of it. A gap smaller than a pixel is its mean (the table's fourth channel).
 *
 * THE ORACLE is world_look.discMarchCpu — the same arithmetic in TypeScript; change the two TOGETHER.
 *
 * A PURE BACKDROP, AT TRUE SCALE. The bounding cylinder stands at the star at its true size (it is the system's size —
 * no impostor), and its clip depth is the FAR PLANE: every body, hull and rock is drawn over it and it never speckles
 * against a planet's impostor (which stands at 100 km whatever its true distance; a disc that used real depth would be
 * cut by every one). The price: haze between the eye and a planet is not drawn over that planet. Not drawn in a
 * headset (a full-screen march per eye).
 */
import * as THREE from 'three/webgpu';
import {
  Fn, Loop, uniform, float, int, vec2, vec3, vec4, texture, positionLocal, positionWorld, cameraPosition, cameraProjectionMatrix, modelViewMatrix, modelWorldMatrix,
  normalize, dot, length, max, min, abs, exp, pow, sqrt, step, fract, floor, mix, clamp, smoothstep, screenCoordinate, screenSize,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { uPlanetSun } from '../planet_look.js';
import type { NaturalEnv } from './natural_api.js';
import type { SystemMakeup } from './natural_types.js';
import {
  DISC_LOOK, DISC_LUT_W, DISC_SUN_FALLOFF, DISC_HAZE_DEPTH, DISC_EMBER_EDGE, DISC_SHEET_MU_MIN, DISC_SHADE_MAX,
  bakeDiscTable, discBounds, discScatterWeight, discEmberColor, type DiscSpec, type DiscKind,
} from './world_look.js';
import { deferDisposeTree } from '../../defer_dispose.js';
import { disposeAfterSwap } from '../../gpu_dispose.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
type N = any;
/** March steps by detail level. */
export const DISC_STEPS: Readonly<Record<'low' | 'high' | 'ultra', number>> = { low: 8, high: 16, ultra: 24 };

export interface DiscSet { count: number; frame(): void; state(): Array<Record<string, unknown>>; dispose(): void }

/** The baked radial table as a half-float texture: R the density, G the scale height ÷ its maximum, B the star's midplane optical depth, A the density with its gaps averaged away. */
function discTexture(spec: DiscSpec): { tex: THREE.DataTexture; rOut: number; hMax: number; kappa: number } {
  const t = bakeDiscTable(spec, DISC_LUT_W);
  const px = new Uint16Array(DISC_LUT_W * 4);
  const hf = (v: number): number => THREE.DataUtils.toHalfFloat(Number.isFinite(v) ? Math.min(DISC_SHADE_MAX, Math.max(0, v)) : 0); // nothing non-finite reaches the table
  for (let i = 0; i < DISC_LUT_W; i++) { px[i * 4] = hf(t.density[i]); px[i * 4 + 1] = hf(t.height[i]); px[i * 4 + 2] = hf(t.shade[i]); px[i * 4 + 3] = hf(t.wide[i]); }
  const tex = new THREE.DataTexture(px, DISC_LUT_W, 1, THREE.RGBAFormat, THREE.HalfFloatType);
  tex.magFilter = THREE.LinearFilter; tex.minFilter = THREE.LinearFilter; tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping; tex.generateMipmaps = false; tex.needsUpdate = true;
  return { tex, rOut: t.rOut, hMax: t.hMax, kappa: t.kappa };
}

export function discMaterial(spec: DiscSpec, steps: number): { material: THREE.Material; tex: THREE.DataTexture; rOut: number; halfH: number } {
  const L = DISC_LOOK[spec.kind];
  const { tex, rOut, hMax, kappa } = discTexture(spec);
  const b = discBounds(spec);
  const halfH = Math.max(b.halfH, hMax * 3);
  // light scattered per unit of light stopped: a thin band is as bright as its `scatter` says (its optical depth is far
  // too small to be seen otherwise); a thick disc scatters its albedo
  const sw = discScatterWeight(spec.kind);
  const col = new THREE.Color(L.color), warm = new THREE.Color(L.warm), emb = discEmberColor(spec.kind);
  const g = L.g, rRef = Math.max(1, spec.innerM), warmLen = Math.max(1, L.warmReach * rOut);
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.BackSide, fog: false });
  mat.blending = THREE.CustomBlending; mat.blendEquation = THREE.AddEquation; mat.blendSrc = THREE.OneFactor; mat.blendDst = THREE.OneMinusSrcAlphaFactor;
  mat.premultipliedAlpha = false; // the light is already weighted by what it passed through (THE ONE PREMULTIPLY would weigh it twice)
  (mat as unknown as { forceSinglePass: boolean }).forceSinglePass = true;
  mat.name = `natural-disc-${spec.kind}`;
  const lut = texture(tex);
  (mat as unknown as { vertexNode: N }).vertexNode = Fn((builder: N) => {
    const clip = cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(positionLocal, 1.0)));
    const r = (builder as { renderer?: { reversedDepthBuffer?: boolean } } | undefined)?.renderer;
    // THE FAR PLANE (0 under reversed depth): behind everything, cut by nothing
    return r?.reversedDepthBuffer === true ? vec4(clip.x, clip.y, float(0), clip.w) : vec4(clip.x, clip.y, clip.w, clip.w);
  })();
  mat.colorNode = Fn(() => {
    const centre = modelWorldMatrix.mul(vec4(0, 0, 0, 1)).xyz;
    const ro = cameraPosition.sub(centre).toVar();                  // the eye about the star, true metres
    const rd = normalize(positionWorld.sub(cameraPosition)).toVar();
    // the ray inside the slab |y| ≤ halfH and the cylinder r ≤ rOut
    const ry = rd.y.add(step(0, rd.y).mul(2e-6).sub(1e-6));
    const ya = float(-halfH).sub(ro.y).div(ry), yb = float(halfH).sub(ro.y).div(ry);
    const a = dot(rd.xz, rd.xz).add(1e-9), bq = dot(ro.xz, rd.xz), cq = dot(ro.xz, ro.xz).sub(rOut * rOut);
    const dq = bq.mul(bq).sub(a.mul(cq));
    const sq = sqrt(max(dq, float(0)));
    const t0 = max(max(min(ya, yb), bq.negate().sub(sq).div(a)), float(0)).toVar();
    const t1 = min(max(ya, yb), bq.negate().add(sq).div(a));
    const dt = max(t1.sub(t0), float(0)).mul(step(0, dq)).div(steps).toVar();
    // one dithered offset per pixel hides the slices (interleaved gradient noise)
    const jit = fract(float(52.9829189).mul(fract(dot(screenCoordinate.xy, vec2(0.06711056, 0.00583715))))).toVar();
    const acc = vec3(0, 0, 0).toVar();
    const T = float(1).toVar();
    const sunC = vec3(uPlanetSun).toVar();
    // THE RING SHEET (world_look.discMarchCpu is the oracle): where the ray crosses the midplane — one point, integrated,
    // never sampled. Everything it reads is placed HERE, before the loop (THE LAZY-SAMPLE LAW).
    const tS = ro.y.negate().div(ry).toVar();
    const muS = max(abs(rd.y), float(DISC_SHEET_MU_MIN)).toVar();
    const rS = length(ro.xz.add(rd.xz.mul(tS))).toVar();
    const tbS = (lut.sample(vec2(rS.div(rOut), 0.5)) as N).level(0).toVar();
    // a gap smaller than a pixel is its mean: the radius one pixel spans on the sheet, against the gap's width
    const pxAng = float(2).div((cameraProjectionMatrix as N).element(int(1)).y.mul(screenSize.y)); // 2 / (cot(fov/2) · rows)
    const gw = max(rS, float(1)).mul(Math.max(L.gapWidth, 0.02));
    const densS = mix(tbS.r, tbS.a, smoothstep(gw.mul(0.35), gw.mul(1.2), tS.mul(pxAng).div(muS)));
    const srcS = pow(pow(float(rRef).div(max(rS, float(rRef))), float(DISC_SUN_FALLOFF)), float(0.55)).mul(L.ember)
      .mul(float(1).sub(smoothstep(float(DISC_EMBER_EDGE[0]), float(DISC_EMBER_EDGE[1]), rS.div(rOut))));
    const inS = step(t0, tS).mul(step(tS, t1)).mul(step(1e-6, dt)).mul(L.sheet > 0 ? 1 : 0);
    const aS = float(1).sub(exp(densS.mul(-L.sheet).div(muS))).mul(inS).toVar();
    const sheetC = vec3(emb[0], emb[1], emb[2]).mul(srcS).mul(aS).toVar();
    // the ONE step that holds the crossing, by index (a ray through the whole slab crosses the midplane exactly on a step
    // boundary: an interval test there finds no step, or two, by rounding — a speckled sheet)
    const iS = clamp(floor(tS.sub(t0).div(max(dt, float(1e-6)))), 0, steps - 1).toVar();
    Loop(steps, ({ i }: { i: N }) => {
      const ti = t0.add(float(i).mul(dt));
      const p = ro.add(rd.mul(ti.add(jit.mul(dt))));
      const r = length(p.xz);
      const tb = (lut.sample(vec2(r.div(rOut), 0.5)) as N).level(0);   // an explicit level: a derivative inside a loop is undefined
      const hz = p.y.div(max(tb.g.mul(hMax), float(1)));
      const gz = exp(hz.mul(hz).mul(-0.5));
      const rho = tb.r.mul(gz);
      const tauStar = tb.b.mul(gz);                                   // the star's light, through the disc's own dust
      const sunT = exp(tauStar.negate());
      const cosT = dot(p.div(max(length(p), float(1))), rd).negate();   // 1 = looking back up the light (forward scattering)
      const ph = min(float(1 - g * g).div(pow(max(float(1 + g * g).sub(cosT.mul(2 * g)), float(1e-4)), float(1.5))), float(9));
      const fall = pow(float(rRef).div(max(r, float(rRef))), float(DISC_SUN_FALLOFF));
      const lit = vec3(col.r, col.g, col.b).mul(sunC).mul(ph.mul(fall).mul(sunT).mul(sw));
      // its own light: the hot inner rim, and the glow of the lit SURFACE layer (the midplane under it is dark)
      const rim = vec3(warm.r, warm.g, warm.b).mul(exp(max(r.sub(spec.innerM), float(0)).div(warmLen).negate()).mul(L.warmGain));
      const haze = vec3(emb[0], emb[1], emb[2]).mul(pow(fall, float(0.55)).mul(L.haze).mul(exp(tauStar.mul(-DISC_HAZE_DEPTH))));
      // ENERGY-CONSERVING STEPS: a step gives its source × what it stops (1 − e^−x; the series where float32 loses it)
      const x = rho.mul(dt).mul(kappa);
      const stop = mix(x.mul(float(1).sub(x.mul(0.5))), float(1).sub(exp(x.negate())), smoothstep(float(1e-4), float(1e-3), x));
      acc.addAssign(lit.add(rim).add(haze).mul(stop).mul(T));
      // the sheet, in the step that holds its crossing: behind the haze in front of it, in front of everything after
      const hit = float(1).sub(step(0.5, abs(float(i).sub(iS))));
      acc.addAssign(sheetC.mul(hit).mul(T).mul(exp(x.mul(clamp(tS.sub(ti).div(max(dt, float(1e-6))), 0, 1)).negate())));
      T.mulAssign(float(1).sub(aS.mul(hit)));
      T.mulAssign(exp(x.negate()));
    });
    return vec4(acc, float(1).sub(T));
  })();
  return { material: markMrtOverlay(mat), tex, rOut, halfH };
}

export function buildDiscs(env: NaturalEnv, makeup: SystemMakeup): DiscSet {
  const root = env.root();
  const list: Array<{ kind: DiscKind; group: THREE.Group; rOut: number; halfH: number; tex: THREE.DataTexture }> = [];
  const steps = DISC_STEPS[env.level];
  if (root && !env.flags.headset) for (const f of makeup.features) {
    if (f.kind !== 'dust_band' && f.kind !== 'debris_disc' && f.kind !== 'proto_disc') continue;
    const spec: DiscSpec = { kind: f.kind, innerM: f.innerM, outerM: f.outerM, gapsM: f.kind === 'proto_disc' ? f.gapsM : [], strength: f.kind === 'dust_band' ? f.strength : 1, seed: f.seed };
    if (!(spec.outerM > spec.innerM) || !Number.isFinite(spec.outerM + spec.innerM)) continue;
    const d = discMaterial(spec, steps);
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(d.rOut * 1.02, d.rOut * 1.02, d.halfH * 2.04, 48, 1), d.material);
    mesh.name = d.material.name;
    mesh.frustumCulled = false;  // it holds the camera as often as not
    mesh.renderOrder = -20;      // first of the translucent things: the star's glare, every tail and shell draw over it
    const group = new THREE.Group();
    group.name = `natural-disc:${f.kind}`;
    const o = env.origin();
    group.position.set(-o.x, -o.y, -o.z); // the star is the origin; a root child at abs − origin (the sector shifts it on a rebase)
    group.add(mesh);
    root.add(group);
    list.push({ kind: f.kind, group, rOut: d.rOut, halfH: d.halfH, tex: d.tex });
  }
  return {
    count: list.length,
    frame: () => { /* nothing moves: the disc stands at the star */ },
    state: () => list.map((d) => ({ kind: d.kind, rOutKm: Math.round(d.rOut / 1000), halfHKm: Math.round(d.halfH / 1000), steps })),
    // the table lives in a texture node (the tree's sweep reads classic slots only): freed after the window like the tree, never in the teardown tick
    dispose: () => { for (const d of list) { deferDisposeTree(d.group); disposeAfterSwap(d.tex); } list.length = 0; },
  };
}
